import { Box, MenuItem, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mdiDotsVertical, mdiAccountEdit } from '@mdi/js';
import { useState } from 'react';
import { useApolloClient, useQuery, useMutation } from '@apollo/client/react';
import { DateTime } from 'luxon';

import {
    ExtraUserFieldsDocument,
    IUserExtraFieldFragment,
    OrganisationDocument,
    UpdateUserDocument,
    DeleteUsersDocument,
    UserDocument,
} from 'graphql/types';
import { PageDrawer } from 'common/components/PageDrawer';
import { Divider } from 'common/components/Divider';
import { InfoRow } from 'common/components/InfoRow';
import { UserPreview } from 'user/components/UserPreview';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { Loader } from 'common/components/Loader';
import { DrawerModuleSection } from 'common/components/DrawerModuleSection';
import { useCanManageUser } from 'user/hooks';
import { Button } from 'common/components/Button';
import { Link } from 'common/components/Link';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { Icon } from 'common/components/Icon';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { useApolloError } from 'common/hooks/useApolloError';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { formatDate } from 'common/utils/formatDate';
import { IGlobalDrawerType } from 'common/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { NotFound } from 'common/components/NotFound';
import { UserActionBar } from 'user/UserActionBar';
import { UserExtendedInfoRows } from 'user/components/UserProfileDrawer/UserExtendedInfoRows';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { IUpdateGroupProps } from 'user/components/GroupUpdateDrawer';

export interface IProps extends IPageDrawerProps {
    groupProps?: IUpdateGroupProps;
    open?: boolean;
    userId: string;
    onArchive?(): void;
    onDelete?(): void;
    onUpdate?(): void;
}

export const UserProfileDrawer = ({
    groupProps,
    open,
    userId,
    onArchive,
    onDelete,
    onUpdate,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [unArchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const { showApolloError } = useApolloError();
    const client = useApolloClient();

    const { data: extraUserFieldsData } = useQuery(ExtraUserFieldsDocument);

    // Reduce extraUserfields array to object
    const extraUserFields: { [key: string]: IUserExtraFieldFragment } = (
        extraUserFieldsData?.extraUserFields || []
    ).reduce((acc, field) => ({ ...acc, [field.name]: field }), {});

    const {
        canManageUser,
        canEditUser,
        isCurrentUser,
        loading: loadingCanManageUser,
    } = useCanManageUser(userId);

    const {
        data: userData,
        loading: userLoading,
        error: userError,
    } = useQuery(UserDocument, {
        variables: { id: userId },
    });
    const { data: organisationData, loading: organisationLoading } =
        useQuery(OrganisationDocument);
    const { openGlobalDrawer, closeGlobalDrawer } = useGlobalDrawer();
    const [updateUser, { loading: updateUserLoading }] =
        useMutation(UpdateUserDocument);
    const [deleteUsers, { loading: loadingDeleteUsers }] =
        useMutation(DeleteUsersDocument);

    const isGroupEditPage = !!useRouteMatch('GROUP_EDIT');

    if (!userLoading && !!userError) {
        return (
            <PageDrawer open={open}>
                <NotFound />
            </PageDrawer>
        );
    }

    const { user } = userData || {};

    const {
        profileImage,
        name,
        email,
        roles,
        language,
        extraFieldsProfile,
        notifications,
        archived,
        participantGroups,
        managerGroups,
    } = user || {};

    const { organisation } = organisationData || {};

    const extraCategories = !!extraFieldsProfile
        ? JSON.parse(extraFieldsProfile)
        : undefined;
    const extraFieldsInfoRows =
        !!extraCategories && !!extraUserFields
            ? Object.keys<Record<string, string>>(extraCategories)
                  .map((fieldKey) => {
                      let value = extraCategories[fieldKey];
                      const fieldData = extraUserFields[fieldKey];

                      if (
                          !fieldData ||
                          (!canManageUser &&
                              !isCurrentUser &&
                              !fieldData?.public)
                      ) {
                          return null;
                      }

                      if (Array.isArray(value)) {
                          return (
                              <InfoRow
                                  description={fieldKey}
                                  key={`${fieldKey}-${value}`}
                              >
                                  {value.join(', ')}
                              </InfoRow>
                          );
                      }

                      if (fieldData.__typename === 'UserExtraCheckboxField') {
                          value = translate(value ? 'yes' : 'no');
                      }

                      if (
                          fieldData.__typename === 'UserExtraDateField' &&
                          value &&
                          DateTime.fromISO(value).isValid
                      ) {
                          value = formatDate(value, 'dd-MM-yyyy');
                      }

                      return (
                          <InfoRow
                              description={fieldKey}
                              key={`${fieldKey}-${value}`}
                          >
                              {value}
                          </InfoRow>
                      );
                  })
                  .filter(Boolean)
            : undefined;

    const loading = userLoading || organisationLoading || loadingCanManageUser;

    const handleArchiveUser = async () => {
        try {
            await updateUser({
                variables: {
                    id: userId,
                    user: {
                        archived: true,
                    },
                },
                update: (cache) => {
                    cache.evict({
                        id: `User:${userId}`,
                    });

                    // TODO: Dirty fix, need to find a solution to update UI when user is archived
                    if (isGroupEditPage) {
                        client.refetchQueries({
                            include: ['GroupUsers', 'GroupManagers'],
                        });
                    }

                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        setArchiveDialogOpen(false);

        closeGlobalDrawer({
            type: IGlobalDrawerType.UserProfile,
        });

        closeGlobalDrawer({
            type: IGlobalDrawerType.UserUpdate,
        });

        displaySnackbar(translate('userActionSuccess.archive'), {
            variant: 'success',
        });

        onArchive?.();

        return;
    };

    const handleUnarchiveUser = async () => {
        try {
            await updateUser({
                variables: {
                    id: userId,
                    user: {
                        archived: false,
                    },
                },
                update: (cache) => {
                    cache.evict({
                        id: `User:${userId}`,
                    });
                    cache.gc();
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        setArchiveDialogOpen(false);

        closeGlobalDrawer({
            type: IGlobalDrawerType.UserProfile,
        });

        closeGlobalDrawer({
            type: IGlobalDrawerType.UserUpdate,
        });

        displaySnackbar(translate('userActionSuccess.unarchive'), {
            variant: 'success',
        });

        onArchive?.();

        return;
    };

    const handleDeleteUser = async () => {
        try {
            await deleteUsers({
                variables: { ids: [userId] },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        setDeleteDialogOpen(false);

        closeGlobalDrawer({
            type: IGlobalDrawerType.UserProfile,
        });

        closeGlobalDrawer({
            type: IGlobalDrawerType.UserUpdate,
        });

        displaySnackbar(translate('userActionSuccess.delete'), {
            variant: 'success',
        });

        onDelete?.();

        return;
    };

    const userEditMenuItem = (
        <MenuItem
            onClick={() =>
                openGlobalDrawer({
                    type: IGlobalDrawerType.UserUpdate,
                    itemId: userId,
                    props: {
                        onArchive: () => setArchiveDialogOpen(true),
                        onUpdate,
                    },
                })
            }
        >
            {translate('editUser')}
        </MenuItem>
    );

    const showUserActionBar = !archived || canEditUser;

    return (
        <PageDrawer open={open} {...other}>
            {(!user || loading) && <Loader />}

            {!!user && !loading && (
                <Box>
                    <DrawerModuleSection>
                        <UserPreview
                            editable={false}
                            name="profileImage"
                            profileImage={profileImage}
                        />
                    </DrawerModuleSection>

                    {showUserActionBar && (
                        <UserActionBar
                            hideProfile
                            hideChat={archived}
                            hideEmail={archived || !email}
                            hidePortfolio={archived}
                            userId={user.id}
                        >
                            {archived && canEditUser && (
                                <>
                                    <DropdownMenu
                                        anchor={
                                            <Button
                                                startIcon={
                                                    <Icon
                                                        path={mdiDotsVertical}
                                                        size="1.9rem"
                                                    />
                                                }
                                                variant="outlined"
                                            >
                                                {translate('actions')}
                                            </Button>
                                        }
                                        placement="bottom-end"
                                    >
                                        <Box>
                                            <MenuItem
                                                onClick={() =>
                                                    setDeleteDialogOpen(true)
                                                }
                                            >
                                                {translate('deletePermanently')}
                                            </MenuItem>

                                            <MenuItem
                                                onClick={() =>
                                                    setUnarchiveDialogOpen(true)
                                                }
                                            >
                                                {translate('unarchiveUser')}
                                            </MenuItem>
                                        </Box>
                                    </DropdownMenu>

                                    <ConfirmDialog
                                        confirmText={translate(
                                            'userForm.userUnarchiveDialog.buttonText'
                                        )}
                                        loading={updateUserLoading}
                                        open={unArchiveDialogOpen}
                                        title={translate(
                                            'userForm.userUnarchiveDialog.title'
                                        )}
                                        onCancel={() =>
                                            setUnarchiveDialogOpen(false)
                                        }
                                        onClose={() =>
                                            setUnarchiveDialogOpen(false)
                                        }
                                        onConfirm={handleUnarchiveUser}
                                    >
                                        {translate(
                                            'userForm.userUnarchiveDialog.text'
                                        )}
                                    </ConfirmDialog>

                                    <ConfirmDialog
                                        confirmText={translate(
                                            'userForm.userDeleteDialog.buttonText'
                                        )}
                                        loading={loadingDeleteUsers}
                                        open={deleteDialogOpen}
                                        title={translate(
                                            'userForm.userDeleteDialog.title'
                                        )}
                                        onCancel={() =>
                                            setDeleteDialogOpen(false)
                                        }
                                        onClose={() => {
                                            setDeleteDialogOpen(false);
                                        }}
                                        onConfirm={handleDeleteUser}
                                    >
                                        {translate(
                                            'userForm.userDeleteDialog.text'
                                        )}
                                    </ConfirmDialog>
                                </>
                            )}

                            {!archived && (
                                <>
                                    {!isCurrentUser && canEditUser && (
                                        <>
                                            <DropdownMenu
                                                anchor={
                                                    <Button
                                                        startIcon={
                                                            <Icon
                                                                path={
                                                                    mdiDotsVertical
                                                                }
                                                                size="1.9rem"
                                                            />
                                                        }
                                                        variant="outlined"
                                                    >
                                                        {translate('actions')}
                                                    </Button>
                                                }
                                                placement="bottom-end"
                                            >
                                                {userEditMenuItem}

                                                <Box>
                                                    <Divider sx={{ py: 1 }} />

                                                    <MenuItem
                                                        onClick={() =>
                                                            setArchiveDialogOpen(
                                                                true
                                                            )
                                                        }
                                                    >
                                                        {translate(
                                                            'archiveUser'
                                                        )}
                                                    </MenuItem>
                                                </Box>
                                            </DropdownMenu>

                                            <ConfirmDialog
                                                confirmText={translate(
                                                    'userForm.userArchiveDialog.buttonText'
                                                )}
                                                loading={updateUserLoading}
                                                open={archiveDialogOpen}
                                                title={translate(
                                                    'userForm.userArchiveDialog.title'
                                                )}
                                                onCancel={() =>
                                                    setArchiveDialogOpen(false)
                                                }
                                                onClose={() =>
                                                    setArchiveDialogOpen(false)
                                                }
                                                onConfirm={handleArchiveUser}
                                            >
                                                {translate(
                                                    'userForm.userArchiveDialog.text'
                                                )}
                                            </ConfirmDialog>
                                        </>
                                    )}

                                    {isCurrentUser && (
                                        <Button
                                            startIcon={
                                                <Icon
                                                    path={mdiAccountEdit}
                                                    size="1.9rem"
                                                />
                                            }
                                            sx={{ flex: 1 }}
                                            variant="outlined"
                                            onClick={() => {
                                                openGlobalDrawer({
                                                    type: IGlobalDrawerType.UserUpdate,
                                                    itemId: userId,
                                                    props: {
                                                        onArchive: () =>
                                                            setArchiveDialogOpen(
                                                                true
                                                            ),
                                                    },
                                                });
                                            }}
                                        >
                                            {translate('editProfile')}
                                        </Button>
                                    )}
                                </>
                            )}
                        </UserActionBar>
                    )}

                    <Typography
                        sx={{ px: { xs: 2, sm: 4 }, py: 4 }}
                        variant="h3"
                    >
                        {name}
                    </Typography>

                    <Stack spacing={2} sx={{ px: { xs: 2, sm: 4 }, pb: 3 }}>
                        {archived && (
                            <InfoRow description={translate('status')}>
                                <Box sx={{ color: 'error.main' }}>
                                    {translate('archived')}
                                </Box>
                            </InfoRow>
                        )}

                        {(canManageUser || isCurrentUser) && !!email && (
                            <InfoRow description={translate('email')}>
                                <Link href={`mailto:${email}`}>{email}</Link>
                            </InfoRow>
                        )}

                        {!!participantGroups?.length && (
                            <InfoRow description={translate('groups')}>
                                {participantGroups.map((participantGroup) => {
                                    const { id, name, permissions } =
                                        participantGroup;
                                    const permissionsObj = JSON.parse(
                                        permissions || '{}'
                                    );

                                    if (permissionsObj.canUpdate) {
                                        return (
                                            <Link
                                                key={id}
                                                sx={{
                                                    textDecoration: 'underline',
                                                    display: 'block',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => {
                                                    openGlobalDrawer({
                                                        type: IGlobalDrawerType.Group,
                                                        itemId: id,
                                                        props: {
                                                            ...groupProps,
                                                            onClose: () =>
                                                                closeGlobalDrawer(
                                                                    {
                                                                        type: IGlobalDrawerType.Group,
                                                                    }
                                                                ),
                                                        },
                                                    });
                                                }}
                                            >
                                                {name}
                                            </Link>
                                        );
                                    }

                                    return <Box key={id}>{name}</Box>;
                                })}
                            </InfoRow>
                        )}

                        {!!managerGroups?.length && (
                            <InfoRow description={translate('managerGroups')}>
                                {managerGroups.map((managerGroup) => {
                                    const { id, name, permissions } =
                                        managerGroup;
                                    const permissionsObj = JSON.parse(
                                        permissions || '{}'
                                    );

                                    if (permissionsObj.canUpdate) {
                                        return (
                                            <Link
                                                key={id}
                                                sx={{
                                                    textDecoration: 'underline',
                                                    display: 'block',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => {
                                                    openGlobalDrawer({
                                                        type: IGlobalDrawerType.Group,
                                                        itemId: id,
                                                        props: {
                                                            ...groupProps,
                                                            onClose: () =>
                                                                closeGlobalDrawer(
                                                                    {
                                                                        type: IGlobalDrawerType.Group,
                                                                    }
                                                                ),
                                                        },
                                                    });
                                                }}
                                            >
                                                {name}
                                            </Link>
                                        );
                                    }

                                    return <Box key={id}>{name}</Box>;
                                })}
                            </InfoRow>
                        )}

                        {(canManageUser || isCurrentUser) &&
                            !!roles?.length && (
                                <InfoRow description={translate('rolesTitle')}>
                                    {roles
                                        .map((role) =>
                                            translate(
                                                `roles.${role?.toLowerCase()}`
                                            )
                                        )
                                        .join(`, `)}
                                </InfoRow>
                            )}

                        {!!language && language !== organisation?.language && (
                            <InfoRow description={translate('language')}>
                                {translate(
                                    `languages.${language.toLowerCase()}`
                                )}
                            </InfoRow>
                        )}

                        {extraFieldsInfoRows}

                        {(canManageUser || isCurrentUser) &&
                            !!notifications && (
                                <InfoRow
                                    description={translate(
                                        'notificationsTitle'
                                    )}
                                >
                                    {translate(
                                        `userNotifications.${notifications.toLowerCase()}`
                                    )}
                                </InfoRow>
                            )}

                        {canManageUser && (
                            <UserExtendedInfoRows userId={userId} />
                        )}
                    </Stack>
                </Box>
            )}
        </PageDrawer>
    );
};
